import * as React from 'react'
import { useEffect, useState } from 'react';
import { CPProblemDisplay } from '../models/CPProblem';
import { CPPartDisplay } from '../models/CPPart';
import ProblemDisplay from './ProblemDisplay';
import { CPAssignmentDisplay } from '../models/CPAssignment';
import { isPrimitive } from 'util';
const cloneDeep = require('lodash.clonedeep')

export interface AssignmentDisplayProps {
    assignment: CPAssignmentDisplay
}

const AssignmentDisplay = ({ assignment }: AssignmentDisplayProps) => {
    const [problems, setProblems] = useState(assignment.problems)
    const [canSubmitLate, setCanSubmitLate] = useState(assignment.canSubmitLate)
    const [dueDate, setDueDate] = useState<Date | undefined>(assignment.dueDate ? new Date(assignment.dueDate.toString()) : undefined);
    const [guid, setGuid] = useState(assignment.guid)
    const [isPreview, setIsPreview] = useState(assignment.isPreview)
    const [isPublic, setIsPublic] = useState(assignment.isPublic)
    const [latePenaltyDate, setLatePenaltyDate] = useState<Date | undefined>(assignment.latePenaltyDate ? new Date(assignment.latePenaltyDate.toString()) : undefined)
    const [lateSubmissionPenaltyPercent, setLateSubmissionPenaltyPercent] = useState(assignment.lateSubmissionPenaltyPercent)
    const [name, setName] = useState(assignment.name)
    const [overview, setOverview] = useState(assignment.overview)
    const [status, setStatus] = useState(assignment.status)
    const [teacherMessage, setTeacherMessage] = useState(assignment.teacherMessage)
    const [totalAssignmentPoints, setTotalAssignmentPoints] = useState(assignment.totalAssignmentPoints)
    const [displayedProblems, setDisplayedProblems] = useState<Array<boolean>>([])
    var now = new Date();
    var serverTime = new Date(assignment.serverTime.toString());
    
    const [nowOffset, setNowOffset] = useState((serverTime.getTime() - now.getTime()));

    // is due date past
    const SubmittableCheck = (): boolean => {
        var now = new Date().getTime() + nowOffset;
        return (now < dueDate!.getTime() || (canSubmitLate && latePenaltyDate == undefined) || (canSubmitLate && latePenaltyDate != undefined && now < latePenaltyDate.getTime())) == true;
    }
    let initialSubmittable = false;
    if (isPublic || isPreview || dueDate == undefined) {
        initialSubmittable = true;
    } else {
        initialSubmittable = SubmittableCheck();
        useEffect(() => {
            const unsubmittableCheck = setInterval(() => {
                if (isSubmittable) {
                    if (!SubmittableCheck()) {
                        setIsSubmittable(false);
                    }
                }
            }, 1000);
            return () => clearInterval(unsubmittableCheck);
        })
    }
    const [isSubmittable, setIsSubmittable] = useState(initialSubmittable);

    useEffect(() => {
        var updatedDisplayedProblems = Array<boolean>(problems.length)
        problems.forEach((problem, problemIndex) => {
            var completedParts = problem.parts.filter((part, partIndex) => {
                return part.answers[part.answers.length - 1]?.isCorrect || (part.answers[part.answers.length - 1]?.isSubmitted && part.answers.length == part.maxAttempts)
            })
            if (completedParts.length == problem.parts.length) {

                updatedDisplayedProblems[problemIndex] = false
            }
            else {
                updatedDisplayedProblems[problemIndex] = true
            }
        })
        setDisplayedProblems(updatedDisplayedProblems)
    }, [])

    const toggleShowProblem = problemIndex => {
        var updatedDisplayedProblems = cloneDeep(displayedProblems)
        updatedDisplayedProblems[problemIndex] = !updatedDisplayedProblems[problemIndex]
        setDisplayedProblems(updatedDisplayedProblems)
    }

    const currentScore = () => {
        var score = 0
        problems.forEach(problem => {
            problem.parts.forEach(part => {
                score += calculatePartScore(part)
            })
        })
        return Math.round(100 * score) / 100
    }

    const totalScore = () => {
        if (assignment.totalAssignmentPoints == 0 || assignment.totalAssignmentPoints == undefined) {
            var score = 0
            problems.forEach(problem => {
                problem.parts.forEach(part => {
                    score += part.points!
                })
            })
            return Math.round(100 * score) / 100
        }
        else {
            return assignment.totalAssignmentPoints
        }
    }

    const calculatePartScore = (part: CPPartDisplay) => {
        var score = 0;
        if (part.answers.length > 0) {
            var lastAnswerIndex = getLastSubmittedAttemptIndex(part)
            if (lastAnswerIndex != -1) {
                var lastAnswer = part.answers[lastAnswerIndex]
                if (lastAnswer.isCorrect) {
                    var lastAnswerDate;
                    if (!isPreview && !isPublic) {
                        lastAnswerDate = new Date(lastAnswer.answerDate.toString())
                    }
                    if (isPreview || isPublic || (dueDate != undefined && lastAnswerDate.getTime() < dueDate.getTime()) || (assignment.canSubmitLate && latePenaltyDate == undefined) || (assignment.canSubmitLate && latePenaltyDate != undefined && lastAnswerDate.getTime() < latePenaltyDate.getTime())) {
                        if (part.unpenalizedAttempts == 0 || lastAnswerIndex + 1 <= part.unpenalizedAttempts!) {
                            score = part.points!;
                        }
                        else {
                            var penaltyPercent = (lastAnswerIndex + 1 - part.unpenalizedAttempts!) * part.penalty!
                            if (penaltyPercent > 100) {
                                penaltyPercent = 100
                            }
                            score = (part.points! * (100 - penaltyPercent)) / 100
                        }
                        if (lateSubmissionPenaltyPercent != undefined && dueDate != undefined) {
                            if (lastAnswerDate.getTime() > dueDate.getTime()) {
                                score = (score * (100 - lateSubmissionPenaltyPercent) / 100)
                            }
                        }
                    }
                }
            }
        }
        return score;
    }


    const displayProblemScore = (problem: CPProblemDisplay) => {
        var correctScore = 0
        var possibleScore = 0
        problem.parts.forEach(part => {
            correctScore += calculatePartScore(part)
            possibleScore += part.points || 0
        })
        return correctScore + "/" + possibleScore
    }


    const changeProblem = (problem, problemIndex) => {
        var updatedProblems = cloneDeep(problems)
        updatedProblems[problemIndex] = cloneDeep(problem)
        setProblems(updatedProblems)
    }

    const getLastSubmittedAttemptIndex = (part: CPPartDisplay) => {
        var index = -1
        var submittedAttempts = part.answers.filter(attempt => attempt.isSubmitted)
        if (submittedAttempts.length > 0) {
            index = part.answers.findIndex(attempt => attempt == submittedAttempts[submittedAttempts.length - 1])
        }
        return index
    }

    return (
        <div>
            <h3 className="align-content-center">{name}</h3>
            {isPublic ?
                <div className="alert alert-warning alert-dismissible fade show" role="alert">
                    <b>This is the guest version of the assignment. Progress will not be saved. If you are part of a Task Tracker class, log in and access this task from your <a href="/Account/Tasks">Tasks and Classes</a> (Start This Task).</b>
                    <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
                </div> : ""
            }
            {!isPublic && !isPreview ?
                <div className="row border rounded mx-0 text-center my-3">
                    <div className="col-4">
                        <div className="row card-header">
                            <b>Due Date</b>
                        </div>
                        <div className="row card-body">
                            <span className={!isSubmittable ? "alert alert-danger" : "" }>
                                {dueDate ? dueDate.toDateString() + " " + dueDate.toLocaleTimeString("en-US") : ""} {!isSubmittable ? " [Closed]" : ""}
                            </span>
                        </div>
                    </div>
                    <div className="col-8">
                        <div className="row card-header">
                            <b>Teacher Message</b>
                        </div>
                        <div className="row card-body" dangerouslySetInnerHTML={{ __html: teacherMessage ?? "" }}>
                        </div>
                    </div>
                </div> : ""}
            {/* Applying similar styling as card for table works better than using card bootstrap class */}
            <div className="row border rounded mx-0 text-center my-3">
                <div className="col col-auto">
                    <div className="row card-header">
                        <b>Problem</b>
                    </div>
                    <div className="row card-body justify-content-center">
                        Points
                    </div>
                </div>
                {
                    problems.map((problem, problemIndex) => {
                        return (
                            <div key={problemIndex} className="col">
                                <div className="row card-header">
                                    <a href={"#problem" + (problemIndex + 1)}><b>{problemIndex + 1}</b></a>
                                </div>
                                <div className="row card-body justify-content-center">
                                    {displayProblemScore(problem)}
                                </div>
                            </div>
                        )
                    })}
                <div className="col">
                    <div className="row card-header text-nowrap">
                        <b>Total Score</b>
                    </div>
                    <div className="row card-body justify-content-center">
                        {assignment.totalAssignmentPoints != 0 ?
                            <>{currentScore()}/{totalScore()}</>
                            : currentScore()}
                    </div>
                </div>
                {assignment.totalAssignmentPoints != 0 ?
                    <div className="col">
                        <div className="row card-header">
                            <b>Percentage</b>
                        </div>
                        <div className="row card-body justify-content-center">
                            {totalScore() != 0 ? Math.round(100 * (100 * currentScore() / totalScore())) / 100 : ""} %
                        </div>
                    </div> : ""}
            </div>
            <div className="card my-3">
                <div className="card-header">
                    <b>Assignment Overview</b>
                </div>
                <div className="card-body" dangerouslySetInnerHTML={{ __html: overview }} />
            </div>
            {
                problems.map((problem, problemIndex) => {
                    return (
                        <ProblemDisplay key={problemIndex}
                            isPreview={isPreview}
                            isPublic={isPublic}
                            problem={problem}
                            problemIndex={problemIndex}
                            toggleShowProblem={toggleShowProblem}
                            displayProblem={displayedProblems[problemIndex]}
                            displayProblemScore={displayProblemScore}
                            changeProblemInAssignment={changeProblem}
                            taskPartGuid={assignment.taskPartGuid}
                            canSubmitLate={canSubmitLate ?? false}
                            lateSubmissionPenalty={lateSubmissionPenaltyPercent}
                            latePenaltyDate={latePenaltyDate}
                            dueDate={dueDate}
                            isSubmittable={isSubmittable}
                        />
                    )
                })
            }
        </div >
    )
}

export default AssignmentDisplay